import React, { useEffect, useState } from "react";
import {
  adminAddVideo,
  adminDeleteItem,
  adminUploadImages,
  fetchCategories,
  fetchGallery,
  login,
  logout,
  resolveMediaUrl,
} from "../../api/galleryApi";

/** List everything (same as public “All Videos” / unfiltered images). Not a real DB category. */
const LIST_ALL = "__ALL_LIST__";

/** Last path segment, decoded (e.g. /uploads/images/foo%20bar.jpg → foo bar.jpg) */
function fileNameFromUrl(url) {
  if (!url || typeof url !== "string") return "";
  try {
    if (url.startsWith("http://") || url.startsWith("https://")) {
      const u = new URL(url);
      const seg = u.pathname.split("/").filter(Boolean).pop();
      return seg ? decodeURIComponent(seg) : "";
    }
    const last = url.split("/").filter(Boolean).pop() || "";
    return decodeURIComponent(last);
  } catch {
    return "";
  }
}

function AdminGallery() {
  const [imageCategories, setImageCategories] = useState([]);
  const [videoCategories, setVideoCategories] = useState([]);
  const [type, setType] = useState("image");
  const specificCategories = type === "image" ? imageCategories : videoCategories;
  const listFilterOptions =
    specificCategories.length > 0 ? [LIST_ALL, ...specificCategories] : [];

  const [auth, setAuth] = useState({ username: "", password: "" });
  const [token, setToken] = useState("");
  const [authError, setAuthError] = useState("");

  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const limit = 20;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [files, setFiles] = useState([]);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [videoThumbnailFile, setVideoThumbnailFile] = useState(null);
  const [videoThumbInputKey, setVideoThumbInputKey] = useState(0);
  const [previewUrl, setPreviewUrl] = useState("");

  async function load() {
    setError("");
    try {
      const res = await fetchGallery({
        type,
        category: category === LIST_ALL ? undefined : category,
        page,
        limit,
      });
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (err) {
      setError(err?.message || "Failed to load gallery");
      setItems([]);
      setTotal(0);
    }
  }

  useEffect(() => {
    let mounted = true;
    (async () => {
      const data = await fetchCategories();
      if (!mounted) return;
      setImageCategories(data.imageCategories || []);
      setVideoCategories(data.videoCategories || []);
    })().catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const list = type === "image" ? imageCategories : videoCategories;
    if (!list.length) {
      setCategory("");
      return;
    }
    setCategory(LIST_ALL);
    setPage(1);
  }, [type, imageCategories, videoCategories]);

  useEffect(() => {
    if (!category) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, category, page]);

  const pageCount = Math.max(1, Math.ceil(total / limit));

  async function onUploadImages(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (category === LIST_ALL) {
        throw new Error(
          'Pick a specific category in the list filter (not "All") before uploading.'
        );
      }
      if (!category) throw new Error("Select a category");
      if (!files.length) throw new Error("Select at least one image");
      if (!token) throw new Error("Please login first");
      await adminUploadImages({ category, files, token });
      setFiles([]);
      await load();
    } catch (err) {
      setError(err?.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  async function onAddVideo(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (category === LIST_ALL) {
        throw new Error(
          'Pick a specific category in the list filter (not "All") before adding a video.'
        );
      }
      if (!category) throw new Error("Select a category");
      if (!youtubeUrl.trim()) throw new Error("Enter a YouTube URL");
      if (!token) throw new Error("Please login first");
      await adminAddVideo({
        category,
        youtube_url: youtubeUrl.trim(),
        thumbnailFile: videoThumbnailFile || undefined,
        token,
      });
      setYoutubeUrl("");
      setVideoThumbnailFile(null);
      setVideoThumbInputKey((k) => k + 1);
      await load();
    } catch (err) {
      setError(err?.message || "Add video failed");
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(id) {
    const ok = window.confirm("Delete this item?");
    if (!ok) return;
    setBusy(true);
    setError("");
    try {
      if (!token) throw new Error("Please login first");
      await adminDeleteItem({ id, token });
      await load();
    } catch (err) {
      setError(err?.message || "Delete failed");
    } finally {
      setBusy(false);
    }
  }

  async function onLogin(e) {
    e.preventDefault();
    setBusy(true);
    setAuthError("");
    try {
      if (!auth.username || !auth.password) throw new Error("Enter username and password");
      const res = await login({ username: auth.username, password: auth.password });
      setToken(res.token || "");
      if (!res.token) throw new Error("Login failed");
    } catch (err) {
      setAuthError(err?.message || "Login failed");
      setToken("");
    } finally {
      setBusy(false);
    }
  }

  async function onLogout() {
    setBusy(true);
    setAuthError("");
    try {
      await logout();
    } catch {
      // ignore
    } finally {
      setToken("");
      setAuth({ username: "", password: "" });
      setBusy(false);
    }
  }

  return (
    <div className="container py-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-3xl font-extrabold text-[#45b3df]">Admin Gallery</h1>
      </div>

      <div className="mt-6 p-4 border rounded-lg bg-white">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="text-lg font-bold text-gray-800">Admin Login</div>
          {token ? (
            <button
              className="px-3 py-2 rounded bg-gray-200"
              onClick={onLogout}
              disabled={busy}
            >
              Logout
            </button>
          ) : null}
        </div>

        {!token ? (
          <form className="mt-3 grid md:grid-cols-3 gap-3" onSubmit={onLogin}>
            <input
              className="border rounded px-3 py-2"
              placeholder="Username"
              value={auth.username}
              onChange={(e) => setAuth((a) => ({ ...a, username: e.target.value }))}
              disabled={busy}
            />
            <input
              className="border rounded px-3 py-2"
              placeholder="Password"
              type="password"
              value={auth.password}
              onChange={(e) => setAuth((a) => ({ ...a, password: e.target.value }))}
              disabled={busy}
            />
            <button
              className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
              type="submit"
              disabled={busy}
            >
              Login
            </button>
          </form>
        ) : (
          <div className="mt-3 text-green-700 font-bold">Logged in</div>
        )}

        {authError ? (
          <div className="mt-3 text-sm text-red-700">{authError}</div>
        ) : null}
      </div>

      {!token ? null : (
        <>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-3">
              <label className="font-bold text-gray-700">Type</label>
              <select
                className="border rounded px-3 py-2"
                value={type}
                onChange={(e) => setType(e.target.value)}
                disabled={busy}
              >
                <option value="image">Images</option>
                <option value="video">Videos</option>
              </select>
            </div>

            <div className="flex items-center gap-3">
              <label className="font-bold text-gray-700">List filter</label>
              <select
                className="border rounded px-3 py-2 max-w-[min(100%,280px)]"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={busy}
              >
                {listFilterOptions.map((c) => (
                  <option key={c} value={c}>
                    {c === LIST_ALL
                      ? type === "video"
                        ? "All videos (same as public site)"
                        : "All images (same as public site)"
                      : c}
                  </option>
                ))}
              </select>
            </div>

            <div className="ml-auto text-sm text-gray-600">
              Total: <span className="font-bold">{total}</span>
            </div>
          </div>

      {error ? (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
          {error}
        </div>
      ) : null}

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="p-4 border rounded-lg bg-white">
          <h2 className="text-xl font-bold text-gray-800">
            Add {type === "image" ? "Images" : "Video"}
          </h2>

          {type === "image" ? (
            <form className="mt-4" onSubmit={onUploadImages}>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
                disabled={busy}
              />
              <div className="mt-3">
                <button
                  className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
                  disabled={busy}
                  type="submit"
                >
                  Upload
                </button>
              </div>
            </form>
          ) : (
            <form className="mt-4 space-y-3" onSubmit={onAddVideo}>
              <input
                className="w-full border rounded px-3 py-2"
                placeholder="YouTube URL (watch or share link)"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                disabled={busy}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Thumbnail (optional)
                </label>
                <input
                  key={videoThumbInputKey}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  disabled={busy}
                  onChange={(e) =>
                    setVideoThumbnailFile(e.target.files?.[0] || null)
                  }
                />
                <p className="text-xs text-gray-500 mt-1">
                  If omitted, the YouTube default image is used. Custom images are
                  stored with this video only.
                </p>
              </div>
              <div>
                <button
                  className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
                  disabled={busy}
                  type="submit"
                >
                  Add Video
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="p-4 border rounded-lg bg-white">
          <h2 className="text-xl font-bold text-gray-800">Items</h2>

          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
            {items.map((it) => (
              <div key={it.id} className="border rounded-lg overflow-hidden">
                {it.type === "image" ? (
                  <img
                    src={resolveMediaUrl(it.file_url)}
                    alt=""
                    className="w-full h-32 object-cover"
                  />
                ) : (
                  <img
                    src={resolveMediaUrl(it.thumbnail_url)}
                    alt=""
                    className="w-full h-32 object-cover"
                  />
                )}

                <div className="p-2 text-xs text-gray-700">
                  <div className="font-bold truncate">{it.category}</div>
                  <div className="truncate">{it.type}</div>
                  {it.type === "image" && it.file_url ? (
                    <div
                      className="mt-1 text-[11px] text-gray-500 break-all"
                      title={fileNameFromUrl(it.file_url)}
                    >
                      {fileNameFromUrl(it.file_url)}
                    </div>
                  ) : null}
                  {it.type === "video" &&
                  it.thumbnail_url &&
                  !it.thumbnail_url.startsWith("http") ? (
                    <div
                      className="mt-1 text-[11px] text-gray-500 break-all"
                      title={fileNameFromUrl(it.thumbnail_url)}
                    >
                      {fileNameFromUrl(it.thumbnail_url)}
                    </div>
                  ) : null}
                </div>

                {it.type === "video" ? (
                  <button
                    className="w-full py-2 text-sm font-bold bg-blue-600 text-white disabled:opacity-50"
                    onClick={() => setPreviewUrl(it.youtube_url)}
                    disabled={busy}
                  >
                    Preview
                  </button>
                ) : null}

                <button
                  className="w-full py-2 text-sm font-bold bg-red-600 text-white disabled:opacity-50"
                  onClick={() => onDelete(it.id)}
                  disabled={busy}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between">
            <button
              className="px-3 py-2 bg-gray-200 rounded disabled:opacity-40"
              disabled={busy || page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <div className="text-sm text-gray-700">
              Page <span className="font-bold">{page}</span> /{" "}
              <span className="font-bold">{pageCount}</span>
            </div>
            <button
              className="px-3 py-2 bg-gray-200 rounded disabled:opacity-40"
              disabled={busy || page >= pageCount}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
      )}
        </>
      )}

      {previewUrl ? (
        <div className="fixed bg-black bg-opacity-80 inset-0 z-50 p-4 md:p-5">
          <div className="relative w-full h-full mx-auto">
            <div className="w-full md:w-7/12 lg:w-6/12 absolute left-1/2 transform -translate-x-1/2 h-auto max-h-full top-1/2 -translate-y-1/2 rounded-lg">
              <button
                aria-label="Close preview"
                className="top-[5%] fixed z-10 inline-flex items-center justify-center w-8 h-8 rounded-full bg-pink-200 ring-4 ring-blue-200 hover:bg-pink-400 hover:ring-blue-400 transition duration-200 right-0"
                onClick={() => setPreviewUrl("")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="white"
                  className="w-8 h-6"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              <div className="h-full overflow-y-auto rounded-lg">
                <div className="w-full px-5 py-5 mx-auto overflow-hidden rounded-lg md:w-full sm:px-8 bg-white">
                  <iframe
                    width="100%"
                    height="500"
                    className="rounded-lg"
                    src={previewUrl}
                    title="YouTube video preview"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default AdminGallery;

